import React from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Stack, Typography } from '@mui/material';

import ChannelMarkupActiveMarkupsAccordion from './ChannelMarkupActiveMarkupsAccordion';
import ChannelMarkupClientInitSection from './ChannelMarkupClientInitSection';
import ChannelMarkupEvaluationContextSection from './ChannelMarkupEvaluationContextSection';
import type { ChannelMarkupTesterPanelProps } from './ChannelMarkupTesterPanel.types';
import ChannelMarkupTesterRunSection from './ChannelMarkupTesterRunSection';

export default function ChannelMarkupTesterPanel({
  title,
  description,
  summaryId,
  contentId,
  showActiveMarkups,
  activeChannelMarkups,
  isActiveChannelMarkupsLoading,
  testerMarkupMode,
  setTesterMarkupMode,
  onRunPreview,
  selectedCurrencies,
  setSelectedCurrencies,
  isClientInitReady,
  clientInitUpdatedAt,
  clientInitRefreshMessage,
  clientInitRefreshError,
  clientInitConfig,
  isClientInitFetching,
  clientInitMode,
  setClientInitMode,
  tenantBrand,
  handleRefreshClientInit,
  testerResults,
  testerResult,
  testerValues,
  effectiveTesterContext,
  registerTester,
  setTesterValue,
  subscriberTierOptions,
  userRoleOptions,
  currencyOptions,
  audienceSearchInput,
  setAudienceSearchInput,
  audienceSearchEnabled,
  audienceOptions,
  isAudienceSearchLoading,
  noAudienceResults,
}: ChannelMarkupTesterPanelProps) {
  const isGeneralModeUnavailable = testerMarkupMode === 'general' && !isClientInitReady;

  return (
    <Accordion defaultExpanded={false}>
      <AccordionSummary aria-controls={contentId} id={summaryId}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined">
            Run this in order: load client init, confirm the evaluation context defaults, then inspect each channel
            markup result in the selected preview region.
          </Alert>
          {showActiveMarkups && testerMarkupMode === 'general' && (
            <ChannelMarkupActiveMarkupsAccordion
              contentId={contentId}
              summaryId={summaryId}
              activeChannelMarkups={activeChannelMarkups}
              isActiveChannelMarkupsLoading={isActiveChannelMarkupsLoading}
            />
          )}
          <ChannelMarkupClientInitSection
            clientInitUpdatedAt={clientInitUpdatedAt}
            clientInitRefreshMessage={clientInitRefreshMessage}
            clientInitRefreshError={clientInitRefreshError}
            clientInitConfig={clientInitConfig}
            isClientInitFetching={isClientInitFetching}
            clientInitMode={clientInitMode}
            setClientInitMode={setClientInitMode}
            tenantBrand={tenantBrand}
            handleRefreshClientInit={handleRefreshClientInit}
          />
          <ChannelMarkupEvaluationContextSection
            testerValues={testerValues}
            registerTester={registerTester}
            setTesterValue={setTesterValue}
            subscriberTierOptions={subscriberTierOptions}
            userRoleOptions={userRoleOptions}
            audienceSearchInput={audienceSearchInput}
            setAudienceSearchInput={setAudienceSearchInput}
            audienceSearchEnabled={audienceSearchEnabled}
            audienceOptions={audienceOptions}
            isAudienceSearchLoading={isAudienceSearchLoading}
            noAudienceResults={noAudienceResults}
          />
          <ChannelMarkupTesterRunSection
            testerMarkupMode={testerMarkupMode}
            setTesterMarkupMode={setTesterMarkupMode}
            onRunPreview={onRunPreview}
            currencyOptions={currencyOptions}
            selectedCurrencies={selectedCurrencies}
            setSelectedCurrencies={setSelectedCurrencies}
            isClientInitReady={isClientInitReady}
            isClientInitFetching={isClientInitFetching}
            handleRefreshClientInit={handleRefreshClientInit}
            clientInitConfig={clientInitConfig}
            testerResults={testerResults}
            testerResult={testerResult}
            testerValues={testerValues}
            effectiveTesterContext={effectiveTesterContext}
            isGeneralModeUnavailable={isGeneralModeUnavailable}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
